import { isInRange, getFormattedTimeRange } from "./DateFilterPicker";
import { invoiceSummaryModel } from "../../models";
import compact from 'lodash/compact';
import { InvoiceDisplayStatus, InvoicePaymentDisplayStatusType } from 'anchor-scout/types';
export const noPaymentStatus = 'none';
export let InvoiceHeaderItemID;

(function (InvoiceHeaderItemID) {
  InvoiceHeaderItemID["invoiceId"] = "invoiceId";
  InvoiceHeaderItemID["companyName"] = "companyName";
  InvoiceHeaderItemID["status"] = "status";
  InvoiceHeaderItemID["issueDate"] = "issueDate";
  InvoiceHeaderItemID["dueDate"] = "dueDate";
  InvoiceHeaderItemID["total"] = "total";
  InvoiceHeaderItemID["additionalInformation"] = "additionalInformation";
})(InvoiceHeaderItemID || (InvoiceHeaderItemID = {}));

const displayedStatusIdToLabel = {
  issued: 'Issued',
  overdue: 'Overdue',
  paymentInitiated: 'Processing',
  paid: 'Paid',
  voided: 'Voided',
  refunded: 'Refunded',
  refundFailed: 'Refund failed',
  disputed: 'Disputed'
};
const paymentStatusSortOrder = {
  [InvoicePaymentDisplayStatusType.StoppedPayment]: 0,
  [InvoicePaymentDisplayStatusType.PaymentFailed]: 1,
  [InvoicePaymentDisplayStatusType.PaymentMethodNotSet]: 2,
  [InvoicePaymentDisplayStatusType.PaymentMethodUnverified]: 3,
  [InvoicePaymentDisplayStatusType.RequiresPaymentValidation]: 4,
  [InvoicePaymentDisplayStatusType.RequiresPaymentApproval]: 5,
  [InvoicePaymentDisplayStatusType.Refunded]: 6,
  [InvoicePaymentDisplayStatusType.Pending]: 7,
  [InvoicePaymentDisplayStatusType.Processing]: 8,
  [InvoicePaymentDisplayStatusType.Collected]: 9,
  [InvoicePaymentDisplayStatusType.Paid]: 10,
  [InvoicePaymentDisplayStatusType.PaidExternally]: 11,
  [noPaymentStatus]: 12
};
export function sortByPaymentStatus(a, b) {
  const infoA = a.paymentStatus;
  const infoB = b.paymentStatus;
  const timestampA = infoA != null && 'timestamp' in infoA ? infoA.timestamp : 0;
  const timestampB = infoB != null && 'timestamp' in infoB ? infoB.timestamp : 0;
  const idA = infoA ? infoA.type : noPaymentStatus;
  const idB = infoB ? infoB.type : noPaymentStatus;
  const rankA = paymentStatusSortOrder[idA];
  const rankB = paymentStatusSortOrder[idB];

  if (rankA === rankB) {
    return timestampB - timestampA;
  } else {
    return rankA - rankB;
  }
}
export function getPaymentStatusFilterOptions(invoices, isVendor) {
  const options = makeEmptyPaymentFilterOptions(isVendor); // Add counters.

  const optionsMap = new Map(options.map(o => [o.value, o]));
  invoices.forEach(invoice => {
    const value = getMatchingPaymentStatusFilter(invoice);
    const option = optionsMap.get(value);
    option && option.counter++;
  }); // Filter out options with 0 counter.

  return options.filter(opt => opt.counter);
}
export function getMatchingPaymentStatusFilter(invoice) {
  return invoice.paymentStatus ? invoice.paymentStatus.type : noPaymentStatus;
}

function getPaymentStatusFilterText(status, isVendor) {
  switch (status) {
    case InvoicePaymentDisplayStatusType.Collected:
      return 'Collected';

    case InvoicePaymentDisplayStatusType.Paid:
      return 'Deposited';

    case InvoicePaymentDisplayStatusType.PaidExternally:
      return 'Paid externally';

    case InvoicePaymentDisplayStatusType.PaymentFailed:
      return 'Payment failed';

    case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
      return 'Payment method not set';

    case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
      return 'Payment method not verified';

    case InvoicePaymentDisplayStatusType.Pending:
      return 'Issued';

    case InvoicePaymentDisplayStatusType.Processing:
      return 'Payment initiated';

    case InvoicePaymentDisplayStatusType.Refunded:
      return 'Refund initiated';

    case InvoicePaymentDisplayStatusType.RefundFailed:
      return 'Refund failed';

    case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
      return isVendor ? 'Client approval required' : 'Pending your approval';

    case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
      return 'Payment validation required';

    case InvoicePaymentDisplayStatusType.StoppedPayment:
      return 'Client stopped payment';

    case InvoicePaymentDisplayStatusType.Disputed:
      return 'Disputed';
  }
}

function makeEmptyPaymentFilterOptions(isVendor) {
  const options = [InvoicePaymentDisplayStatusType.StoppedPayment, InvoicePaymentDisplayStatusType.RequiresPaymentApproval, InvoicePaymentDisplayStatusType.RequiresPaymentValidation, InvoicePaymentDisplayStatusType.PaymentFailed, InvoicePaymentDisplayStatusType.PaymentMethodNotSet, InvoicePaymentDisplayStatusType.PaymentMethodUnverified, InvoicePaymentDisplayStatusType.Processing, InvoicePaymentDisplayStatusType.Collected, InvoicePaymentDisplayStatusType.Paid, InvoicePaymentDisplayStatusType.PaidExternally, InvoicePaymentDisplayStatusType.Refunded, InvoicePaymentDisplayStatusType.Disputed].map(status => {
    const option = {
      value: status,
      label: getPaymentStatusFilterText(status, isVendor),
      counter: 0
    };
    return option;
  });
  options.push({
    value: noPaymentStatus,
    label: 'None',
    counter: 0
  });
  return options;
}

export function getInvoiceStatusFilterOptions(invoices) {
  const options = compact([InvoiceDisplayStatus.Issued, InvoiceDisplayStatus.PaymentInitiated, InvoiceDisplayStatus.Paid, InvoiceDisplayStatus.Overdue, InvoiceDisplayStatus.Voided, InvoiceDisplayStatus.Refunded, InvoiceDisplayStatus.PaymentDisputed]).map(statusId => ({
    value: statusId,
    label: displayedStatusIdToLabel[statusId],
    counter: 0
  })); // Add counters.

  const optionsMap = {};
  options.forEach(opt => {
    optionsMap[opt.value] = opt;
  });
  invoices.forEach(invoice => {
    optionsMap[invoice.displayedStatus] && optionsMap[invoice.displayedStatus].counter++;
  }); // Filter out options with 0 counter.

  return options.filter(opt => opt.counter);
}

function textSearchInvoice(search, invoice, isVendor) {
  const invoiceNumber = invoice.formattedNumber;
  const companyName = invoiceSummaryModel.otherPartyCompanyName(invoice, isVendor);
  const title = invoiceSummaryModel.relationshipTitle(invoice);
  return "".concat(invoiceNumber, " ").concat(companyName, " ").concat(title || '').toLowerCase().includes(search);
}

export function applyStatusFilter(_ref) {
  let {
    invoices,
    statusFilter
  } = _ref;
  return statusFilter.length ? invoices.filter(inv => statusFilter.includes(inv.displayedStatus)) : invoices.slice();
}
export function applyPaymentStatusFilter(_ref2) {
  let {
    invoices,
    paymentStatusFilter
  } = _ref2;
  const paymentStatusFilteredInvoices = invoices.filter(invoice => {
    return !paymentStatusFilter.length || paymentStatusFilter.includes(getMatchingPaymentStatusFilter(invoice));
  });
  return paymentStatusFilteredInvoices;
}
export function applyDateFilter(_ref3) {
  let {
    invoices,
    dateFilter,
    now
  } = _ref3;
  const dateFilteredInvoices = invoices.filter(invoice => {
    return !dateFilter || isInRange(getFormattedTimeRange(dateFilter, now), invoice.rawCreatedOn);
  });
  return dateFilteredInvoices;
}
export function applyQboStatusFilter(invoices, qboStatusFilter) {
  switch (qboStatusFilter) {
    case QboStatusFilter.All:
      return invoices;

    case QboStatusFilter.Synced:
      return invoices.filter(invoiceSummaryModel.isSynced);

    case QboStatusFilter.NotSynced:
      return invoices.filter(invoiceSummaryModel.isNotSynced);
  }
}
export function getQboStatusFilterOptions(invoices) {
  const options = [{
    value: QboStatusFilter.All,
    label: 'All',
    counter: invoices.length
  }, {
    value: QboStatusFilter.Synced,
    label: 'Synced',
    counter: 0
  }, {
    value: QboStatusFilter.NotSynced,
    label: 'Not synced',
    counter: 0
  }];
  invoices.forEach(inv => {
    invoiceSummaryModel.isSynced(inv) && options[1].counter++;
    invoiceSummaryModel.isNotSynced(inv) && options[2].counter++;
  });
  return options.filter(opt => opt.counter > 0);
}
export function applySearch(_ref4) {
  let {
    invoices,
    isVendor,
    search
  } = _ref4;
  const searchFilteredInvoices = invoices.filter(invoice => {
    const normalizedSearch = search ? search.trim().toLowerCase() : search;
    return !normalizedSearch || textSearchInvoice(normalizedSearch, invoice, isVendor);
  });
  return searchFilteredInvoices;
}
export function applySort(_ref5) {
  let {
    invoices,
    isVendor,
    sort,
    headerItems
  } = _ref5;

  if (sort) {
    var _headerItems$sort$col;

    const colId = (_headerItems$sort$col = headerItems[sort.colIndex]) === null || _headerItems$sort$col === void 0 ? void 0 : _headerItems$sort$col.id;

    if (colId) {
      return invoices.sort(sortFunctions[colId]({
        isVendor,
        isAscending: sort.sort === 'asc'
      }));
    }
  }

  return invoices;
}

const sortByInvoiceNumber = (a, b) => {
  return b.number - a.number;
};

const getCompanyName = function getCompanyName(invoice) {
  let isVendor = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let companyName = invoiceSummaryModel.otherPartyCompanyName(invoice, isVendor);

  if (invoiceSummaryModel.relationshipTitle(invoice)) {
    companyName += " (".concat(invoiceSummaryModel.relationshipTitle(invoice), ")");
  }

  return companyName.toLowerCase();
};

const sortByCompanyName = (a, b, isVendor) => {
  const companyNameA = getCompanyName(a, isVendor);
  const companyNameB = getCompanyName(b, isVendor);

  if (companyNameA < companyNameB) {
    return -1;
  }

  if (companyNameA > companyNameB) {
    return 1;
  }

  return 0;
};

const sortByAmount = (a, b) => {
  return b.rawTotalAmount.minus(a.rawTotalAmount).toNumber();
};

const sortByIssuedOn = (a, b) => {
  return (b.rawIssuedOn || b.rawCreatedOn).getTime() - (a.rawIssuedOn || a.rawCreatedOn).getTime();
};

const sortByDueDate = (a, b) => {
  return b.rawDueDate.getTime() - a.rawDueDate.getTime();
};

const vendorStatusOrder = {
  [InvoiceDisplayStatus.Voided]: 1,
  [InvoiceDisplayStatus.Paid]: 2,
  [InvoiceDisplayStatus.PaymentInitiated]: 3,
  [InvoiceDisplayStatus.Refunded]: 4,
  [InvoiceDisplayStatus.Issued]: 5,
  [InvoiceDisplayStatus.Overdue]: 6,
  [InvoiceDisplayStatus.RefundFailed]: 7,
  [InvoiceDisplayStatus.PaymentDisputed]: 8
};
const clientStatusOrder = {
  [InvoiceDisplayStatus.Voided]: 1,
  [InvoiceDisplayStatus.Paid]: 2,
  [InvoiceDisplayStatus.PaymentInitiated]: 4,
  [InvoiceDisplayStatus.Refunded]: 5,
  [InvoiceDisplayStatus.Issued]: 6,
  [InvoiceDisplayStatus.Overdue]: 7,
  [InvoiceDisplayStatus.RefundFailed]: 8,
  [InvoiceDisplayStatus.PaymentDisputed]: 9
};

const getStatusOrder = (invoice, isVendor) => {
  return isVendor ? vendorStatusOrder[invoice.displayedStatus] : clientStatusOrder[invoice.displayedStatus];
};

const sortByStatus = (a, b, isVendor) => {
  const statusOrderA = getStatusOrder(a, isVendor);
  const statusOrderB = getStatusOrder(b, isVendor);
  return statusOrderB - statusOrderA;
};

const sortById = (a, b) => a.id < b.id ? -1 : 1;

const sortFunctions = {
  [InvoiceHeaderItemID.invoiceId]: _ref6 => {
    let {
      isAscending
    } = _ref6;
    return (a, b) => {
      const retval = sortByInvoiceNumber(a, b) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  },
  [InvoiceHeaderItemID.companyName]: _ref7 => {
    let {
      isAscending,
      isVendor
    } = _ref7;
    return (a, b) => {
      const retval = sortByCompanyName(a, b, isVendor) || sortByAmount(a, b) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  },
  [InvoiceHeaderItemID.status]: _ref8 => {
    let {
      isAscending,
      isVendor
    } = _ref8;
    return (a, b) => {
      const retval = sortByStatus(a, b, isVendor) || sortByIssuedOn(a, b) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  },
  [InvoiceHeaderItemID.issueDate]: _ref9 => {
    let {
      isAscending
    } = _ref9;
    return (a, b) => {
      const retval = sortByIssuedOn(a, b) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  },
  [InvoiceHeaderItemID.dueDate]: _ref10 => {
    let {
      isAscending
    } = _ref10;
    return (a, b) => {
      const retval = sortByDueDate(a, b) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  },
  [InvoiceHeaderItemID.total]: _ref11 => {
    let {
      isAscending,
      isVendor
    } = _ref11;
    return (a, b) => {
      const retval = sortByAmount(a, b) || sortByCompanyName(a, b, isVendor) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  },
  [InvoiceHeaderItemID.additionalInformation]: _ref12 => {
    let {
      isAscending
    } = _ref12;
    return (a, b) => {
      const retval = sortByPaymentStatus(a, b) || sortById(a, b);
      return isAscending ? -retval : retval;
    };
  }
};
export let QboStatusFilter;

(function (QboStatusFilter) {
  QboStatusFilter["All"] = "";
  QboStatusFilter["Synced"] = "synced";
  QboStatusFilter["NotSynced"] = "notSynced";
})(QboStatusFilter || (QboStatusFilter = {}));

export function applyFilters(_ref13) {
  let {
    fullInvoices,
    statusFilter,
    paymentStatusFilter,
    dateFilter,
    qboStatusFilter,
    search,
    isVendor,
    now
  } = _ref13;
  let invoices = fullInvoices;

  if (statusFilter != null) {
    invoices = applyStatusFilter({
      invoices,
      statusFilter
    });
  }

  if (paymentStatusFilter != null) {
    invoices = applyPaymentStatusFilter({
      invoices,
      paymentStatusFilter
    });
  }

  if (dateFilter != null) {
    invoices = applyDateFilter({
      invoices,
      dateFilter,
      now
    });
  }

  if (qboStatusFilter != null) {
    invoices = applyQboStatusFilter(invoices, qboStatusFilter);
  }

  return applySearch({
    invoices,
    search,
    isVendor
  });
}